const roundMoney = (value) => Math.round((Number(value) || 0) * 100) / 100;

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const cloneModifiers = (modifiers = []) =>
  Array.isArray(modifiers) ? modifiers.map((modifier) => ({ ...modifier })) : [];

const normalizeModifiersForPayload = (modifiers = []) =>
  (Array.isArray(modifiers) ? modifiers : [])
    .map((modifier) => ({
      modifierId:
        modifier?.modifierId ||
        modifier?.optionId ||
        modifier?.modifierOptionId ||
        modifier?.productModifierOptionId ||
        modifier?.modifierItemId ||
        null,
      qty: Math.max(1, toNumber(modifier?.qty || 1)),
    }))
    .filter((modifier) => Boolean(modifier.modifierId));

const normalizeRate = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return 0;
  return numeric > 1 ? numeric / 100 : numeric;
};

const getModifierSignature = (modifiers = []) => {
  if (!Array.isArray(modifiers) || modifiers.length === 0) return "none";

  return modifiers
    .map((modifier) => {
      const modifierId =
        modifier?.modifierId ??
        modifier?.optionId ??
        modifier?.modifierOptionId ??
        modifier?.productModifierOptionId ??
        modifier?.modifierItemId ??
        modifier?.option_id ??
        "";
      const groupId =
        modifier?.modifierGroupId ??
        modifier?.groupId ??
        modifier?.group_id ??
        "";
      const qty = Math.max(1, toNumber(modifier?.qty || 1));
      const delta = roundMoney(modifier?.priceDelta ?? modifier?.price ?? 0);
      const fallbackName = String(
        modifier?.name || modifier?.modifierName || modifier?.optionName || "",
      )
        .trim()
        .toLowerCase();
      const identity =
        modifierId || (fallbackName ? `${fallbackName}@${delta}` : "");
      return `${groupId}:${identity}:${qty}:${delta}`;
    })
    .sort()
    .join("|");
};

const getItemId = (line) => line?.itemId || line?.productId || "";

const getQty = (line) => Math.max(0, toNumber(line?.qty ?? line?.Qty));

const getQtySentToKitchen = (line) => {
  const qty = getQty(line);
  const reportedQtySent = Math.max(
    0,
    Math.min(qty, toNumber(line?.qtySentToKitchen)),
  );

  if (reportedQtySent > 0) return reportedQtySent;
  if (line?.isSentToKitchen) return qty;
  return 0;
};

const getLineUnitAmounts = (line) => {
  const qty = Math.max(1, getQty(line));
  const subtotal = toNumber(
    line?.lineSubtotal ?? line?.lineSubTotal ?? line?.subtotal ?? line?.unitPrice,
  );
  const taxAmount = toNumber(line?.taxAmount ?? line?.tax ?? 0);
  const total = toNumber(line?.lineTotal ?? line?.total ?? subtotal + taxAmount);

  return {
    unitSubtotal: roundMoney(subtotal / qty),
    unitTax: roundMoney(taxAmount / qty),
    unitTotal: roundMoney(total / qty),
  };
};

const uid = () =>
  `local:${Date.now().toString(36)}:${Math.random().toString(36).slice(2, 10)}`;

const applyQty = (line, qty) => {
  const safeQty = Math.max(0, toNumber(qty));
  const unitSubtotal = toNumber(line?.__unitSubtotal ?? line?.unitPrice ?? 0);
  const unitTax = toNumber(line?.__unitTax ?? 0);
  const unitTotal =
    toNumber(line?.__unitTotal) || roundMoney(unitSubtotal + unitTax);

  return {
    ...line,
    qty: safeQty,
    lineSubtotal: roundMoney(unitSubtotal * safeQty),
    taxAmount: roundMoney(unitTax * safeQty),
    lineTotal: roundMoney(unitTotal * safeQty),
    __unitSubtotal: unitSubtotal,
    __unitTax: unitTax,
    __unitTotal: unitTotal,
  };
};

const createDraftLineFromExisting = (line, qty, options = {}) => {
  const { unitSubtotal, unitTax, unitTotal } = getLineUnitAmounts(line);
  const safeQty = Math.max(0, toNumber(qty));
  const orderLineId = options.orderLineId ?? line?.orderLineId ?? uid();

  return {
    ...line,
    orderLineId,
    qty: safeQty,
    qtySentToKitchen: 0,
    isSentToKitchen: false,
    lineSubtotal: roundMoney(unitSubtotal * safeQty),
    taxAmount: roundMoney(unitTax * safeQty),
    lineTotal: roundMoney(unitTotal * safeQty),
    modifiers: cloneModifiers(line?.modifiers),
    __unitSubtotal: unitSubtotal,
    __unitTax: unitTax,
    __unitTotal: unitTotal,
    __localOnly: Boolean(options.localOnly),
    __serverLineId: options.serverLineId ?? line?.orderLineId ?? null,
    __serverSentQty: Math.max(0, toNumber(options.serverSentQty ?? 0)),
  };
};

export const buildDraftStateFromOrder = (orderLines = []) => {
  const draftLines = [];

  orderLines.forEach((line) => {
    const qty = getQty(line);
    const qtySent = getQtySentToKitchen(line);
    const qtyPending = Math.max(0, qty - qtySent);

    if (qtyPending > 0) {
      draftLines.push(
        createDraftLineFromExisting(line, qtyPending, {
          localOnly: false,
          serverLineId: line?.orderLineId ?? null,
          serverSentQty: qtySent,
          orderLineId: line?.orderLineId ?? uid(),
        }),
      );
    }
  });

  return draftLines;
};

export const buildSentDisplayLinesFromOrder = (orderLines = []) => {
  const sentLines = [];

  orderLines.forEach((line) => {
    const qtySent = getQtySentToKitchen(line);

    if (qtySent > 0) {
      sentLines.push(
        createDraftLineFromExisting(line, qtySent, {
          localOnly: false,
          serverLineId: line?.orderLineId ?? null,
          serverSentQty: qtySent,
          orderLineId: `${line?.orderLineId ?? uid()}:sent`,
        }),
      );
    }
  });

  return sentLines;
};

export const getLocalDraftStorageKey = (orderId) =>
  `pos.localDraftLines.${orderId}`;

export const readLocalDraft = (orderId) => {
  if (!orderId) return null;

  try {
    const raw = sessionStorage.getItem(getLocalDraftStorageKey(orderId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

export const writeLocalDraft = (orderId, draftLines) => {
  if (!orderId) return;

  try {
    sessionStorage.setItem(
      getLocalDraftStorageKey(orderId),
      JSON.stringify(Array.isArray(draftLines) ? draftLines : []),
    );
  } catch {
    // Ignore storage failures and continue with in-memory state.
  }
};

export const clearLocalDraft = (orderId) => {
  if (!orderId) return;

  try {
    sessionStorage.removeItem(getLocalDraftStorageKey(orderId));
  } catch {
    // Ignore storage failures.
  }
};

export const addItemToLocalDraft = ({
  draftLines = [],
  item,
  modifiers = [],
  fallbackTaxRate = 0,
}) => {
  if (!item?.id) return draftLines;

  const nextLines = Array.isArray(draftLines) ? [...draftLines] : [];
  const normalizedModifiers = cloneModifiers(modifiers);
  const modifierSignature = getModifierSignature(normalizedModifiers);

  const existingIndex = nextLines.findIndex((line) => {
    if (getItemId(line) !== item.id) return false;
    return getModifierSignature(line?.modifiers) === modifierSignature;
  });

  if (existingIndex >= 0) {
    const existing = nextLines[existingIndex];
    nextLines[existingIndex] = applyQty(existing, getQty(existing) + 1);
    return nextLines;
  }

  const modifierDelta = normalizedModifiers.reduce((sum, modifier) => {
    const qty = Math.max(1, toNumber(modifier?.qty || 1));
    const delta = toNumber(modifier?.priceDelta ?? modifier?.price ?? 0);
    return sum + delta * qty;
  }, 0);

  const unitPrice = roundMoney(
    toNumber(item?.basePrice ?? item?.price ?? item?.unitPrice ?? 0) +
      modifierDelta,
  );
  const explicitTaxRate =
    normalizeRate(item?.taxRatePercent) ||
    normalizeRate(item?.taxPercent) ||
    normalizeRate(item?.taxRate) ||
    normalizeRate(item?.ratePercent);
  const appliedTaxRate = explicitTaxRate || normalizeRate(fallbackTaxRate);
  const unitTax = roundMoney(unitPrice * appliedTaxRate);

  nextLines.push(
    applyQty(
      {
        orderLineId: uid(),
        itemId: item.id,
        productId: item.id,
        itemName: item?.name || item?.itemName || "",
        productName: item?.name || item?.itemName || "",
        unitPrice,
        stationCode: item?.stationCode ?? item?.defaultStationCode ?? null,
        notes: "",
        modifiers: normalizedModifiers,
        qtySentToKitchen: 0,
        isSentToKitchen: false,
        __localOnly: true,
        __serverLineId: null,
        __serverSentQty: 0,
        __unitSubtotal: unitPrice,
        __unitTax: unitTax,
        __unitTotal: roundMoney(unitPrice + unitTax),
      },
      1,
    ),
  );

  return nextLines;
};

export const changeGroupedDraftLineQty = ({
  draftLines = [],
  groupedLine,
  delta,
}) => {
  if (!groupedLine || !delta) return draftLines;

  const nextLines = Array.isArray(draftLines) ? [...draftLines] : [];
  const sourceIds = new Set(
    (Array.isArray(groupedLine?.sourceLines) ? groupedLine.sourceLines : [groupedLine])
      .map((line) => String(line?.orderLineId || "")),
  );

  const sourceIndexes = nextLines
    .map((line, index) => ({ line, index }))
    .filter(({ line }) => sourceIds.has(String(line?.orderLineId || "")));

  if (delta > 0) {
    const hasModifiers = getModifierSignature(groupedLine?.modifiers) !== "none";

    if (hasModifiers) {
      nextLines.push(
        createDraftLineFromExisting(groupedLine, 1, {
          localOnly: true,
          serverLineId: null,
          serverSentQty: 0,
          orderLineId: uid(),
        }),
      );
      return nextLines;
    }

    const target = sourceIndexes[0];
    if (!target) {
      nextLines.push(
        createDraftLineFromExisting(groupedLine, 1, {
          localOnly: true,
          serverLineId: null,
          serverSentQty: 0,
          orderLineId: uid(),
        }),
      );
      return nextLines;
    }

    nextLines[target.index] = applyQty(target.line, getQty(target.line) + 1);
    return nextLines;
  }

  const preferredTarget =
    sourceIndexes
      .slice()
      .sort((a, b) => {
        const localRank = Number(Boolean(b.line?.__localOnly)) - Number(Boolean(a.line?.__localOnly));
        if (localRank !== 0) return localRank;
        return getQty(a.line) - getQty(b.line);
      })[0] || null;

  if (!preferredTarget) return nextLines;

  const currentQty = getQty(preferredTarget.line);
  if (currentQty <= 1) {
    return nextLines.filter((_, index) => index !== preferredTarget.index);
  }

  nextLines[preferredTarget.index] = applyQty(
    preferredTarget.line,
    currentQty - 1,
  );
  return nextLines;
};

export const removeGroupedDraftLine = ({ draftLines = [], groupedLine }) => {
  if (!groupedLine) return draftLines;

  const sourceIds = new Set(
    (Array.isArray(groupedLine?.sourceLines) ? groupedLine.sourceLines : [groupedLine])
      .map((line) => String(line?.orderLineId || "")),
  );

  return (Array.isArray(draftLines) ? draftLines : []).filter(
    (line) => !sourceIds.has(String(line?.orderLineId || "")),
  );
};

export const buildCombinedDisplayLines = ({ orderLines = [], draftLines = [] }) => [
  ...buildSentDisplayLinesFromOrder(orderLines),
  ...(Array.isArray(draftLines) ? draftLines : []),
];

export const buildSubmittedOrderSnapshot = (order) => {
  if (!order) return order;

  const nextLines = Array.isArray(order?.lines)
    ? order.lines.map((line) => {
        const qty = getQty(line);
        if (qty <= 0) return line;

        const qtySent = getQtySentToKitchen(line);
        return {
          ...line,
          qtySentToKitchen: Math.max(qtySent, qty),
          isSentToKitchen: true,
        };
      })
    : [];

  return {
    ...order,
    lines: nextLines,
  };
};

export const syncLocalDraftToServer = async ({
  orderId,
  orderLines = [],
  draftLines = [],
  addLine,
  updateLine,
  deleteLine,
  buildLineQtyUpdatePayload,
  buildModifierQtyPatchPayload,
  modifierOptions = [],
}) => {
  let latestOrder = null;
  const persistedLines = new Map();
  orderLines.forEach((line) => {
    if (line?.orderLineId) {
      persistedLines.set(String(line.orderLineId), line);
    }
  });

  const persistedDraftLines = buildDraftStateFromOrder(orderLines);
  const draftByServerId = new Map();

  draftLines.forEach((line) => {
    if (line?.__serverLineId) {
      draftByServerId.set(String(line.__serverLineId), line);
    }
  });

  for (const persistedDraftLine of persistedDraftLines) {
    const serverLineId = String(persistedDraftLine.__serverLineId || "");
    const fullServerLine = persistedLines.get(serverLineId);
    if (!serverLineId || !fullServerLine) continue;

    const nextDraftLine = draftByServerId.get(serverLineId);
    const sentQty = Math.max(0, toNumber(persistedDraftLine.__serverSentQty || 0));

    if (!nextDraftLine || getQty(nextDraftLine) <= 0) {
      if (sentQty > 0) {
        const payload =
          getModifierSignature(fullServerLine?.modifiers) !== "none"
            ? buildModifierQtyPatchPayload({
                line: fullServerLine,
                nextQty: sentQty,
                modifierOptions,
              })
            : buildLineQtyUpdatePayload(fullServerLine, sentQty);

        latestOrder = await updateLine({
          orderId,
          lineId: fullServerLine.orderLineId,
          payload,
        });
      } else {
        latestOrder = await deleteLine({
          orderId,
          lineId: fullServerLine.orderLineId,
        });
      }
      continue;
    }

    const nextQty = sentQty + getQty(nextDraftLine);
    const payload =
      getModifierSignature(fullServerLine?.modifiers) !== "none"
        ? buildModifierQtyPatchPayload({
            line: fullServerLine,
            nextQty,
            modifierOptions,
          })
        : buildLineQtyUpdatePayload(fullServerLine, nextQty);

    latestOrder = await updateLine({
      orderId,
      lineId: fullServerLine.orderLineId,
      payload,
    });
  }

  const localOnlyLines = draftLines.filter((line) => !line?.__serverLineId);
  for (const line of localOnlyLines) {
    const qty = Math.max(0, getQty(line));
    if (qty <= 0) continue;

    const payload = {
      productId: getItemId(line),
      qty: 1,
      notes: line?.notes || "",
      modifiers: normalizeModifiersForPayload(line?.modifiers),
    };

    const hasModifiers = getModifierSignature(line?.modifiers) !== "none";
    if (hasModifiers) {
      for (let index = 0; index < qty; index += 1) {
        latestOrder = await addLine({ orderId, payload });
      }
      continue;
    }

    latestOrder = await addLine({
      orderId,
      payload: {
        ...payload,
        qty,
      },
    });
  }

  return latestOrder;
};
